(function () {
  'use strict';

  var sbbNamespace = window.__SBB__ = window.__SBB__ || {};
  var ScaleBarBuilder = sbbNamespace.ScaleBarBuilder;
  var clamp = sbbNamespace.clamp;
  var parseNumber = sbbNamespace.parseNumber;
  var toFixedTrim = sbbNamespace.toFixedTrim;
  var mmPerCm = sbbNamespace.mmPerCm;
  var mmPerIn = sbbNamespace.mmPerIn;
  var mmPerPt = sbbNamespace.mmPerPt;
  var maxDistanceOffsetMm = sbbNamespace.maxDistanceOffsetMm;
  var paperSizes = sbbNamespace.paperSizes;
  var jsPdfPaperFormats = sbbNamespace.jsPdfPaperFormats;
  var checkerTagPaddingFactor = sbbNamespace.checkerTagPaddingFactor;

  if (!ScaleBarBuilder || !clamp || !parseNumber || !toFixedTrim || !paperSizes || !jsPdfPaperFormats) {
    return;
  }

  var MM_PER_CM = mmPerCm;
  var MM_PER_IN = mmPerIn;
  var MM_PER_PT = mmPerPt;
  var MAX_DISTANCE_OFFSET_MM = maxDistanceOffsetMm;
  var PAPER_SIZES = paperSizes;
  var JSPDF_PAPER_FORMATS = jsPdfPaperFormats;
  var CHECKER_TAG_PADDING_FACTOR = checkerTagPaddingFactor;


  ScaleBarBuilder.prototype.mmToUi = function (mm) {
    if (this.state.unitSystem === 'imperial') {
      return mm / MM_PER_IN;
    }
    return mm;
  };


  ScaleBarBuilder.prototype.uiToMm = function (value) {
    if (this.state.unitSystem === 'imperial') {
      return value * MM_PER_IN;
    }
    return value;
  };


  ScaleBarBuilder.prototype.cmToUi = function (cm) {
    if (this.state.unitSystem === 'imperial') {
      return (cm * MM_PER_CM) / MM_PER_IN;
    }
    return cm;
  };


  ScaleBarBuilder.prototype.uiToCm = function (value) {
    if (this.state.unitSystem === 'imperial') {
      return (value * MM_PER_IN) / MM_PER_CM;
    }
    return value;
  };


  ScaleBarBuilder.prototype.formatUiNumber = function (value, decimals) {
    return toFixedTrim(value, decimals);
  };


  ScaleBarBuilder.prototype.formatCoordinate = function (valueMm, unitSystem, options) {
    var opts = options || {};
    var minDp = Math.max(0, Math.round(parseNumber(opts.minDp, 4)));
    var maxDp = Math.max(minDp, Math.round(parseNumber(opts.maxDp, 8)));
    var unit = unitSystem || this.state.unitSystem || 'metric';
    var converted = unit === 'imperial'
      ? (parseNumber(valueMm, 0) / MM_PER_IN)
      : parseNumber(valueMm, 0);

    var out = '';
    for (var dp = minDp; dp <= maxDp; dp += 1) {
      out = toFixedTrim(converted, dp);
      if (out.indexOf('.') === -1 || out.split('.')[1].length >= minDp) {
        if (dp === maxDp) {
          break;
        }
        var next = toFixedTrim(converted, dp + 1);
        if (next === out) {
          break;
        }
      }
    }
    return out;
  };


  ScaleBarBuilder.prototype.formatDistanceMm = function (distanceMm, options) {
    var opts = options || {};
    var unitSystem = String(opts.unitSystem || this.state.unitSystem || 'metric') === 'imperial' ? 'imperial' : 'metric';
    var metricDp = clamp(Math.round(parseNumber(opts.metricDp, 2)), 0, 6);
    var imperialDp = clamp(Math.round(parseNumber(opts.imperialDp, 3)), 0, 6);
    if (unitSystem === 'imperial') {
      return toFixedTrim(distanceMm / MM_PER_IN, imperialDp) + ' in';
    }
    return toFixedTrim(distanceMm / MM_PER_CM, metricDp) + ' cm';
  };


  ScaleBarBuilder.prototype.ptToMm = function (pt) {
    return parseNumber(pt, 12) * MM_PER_PT;
  };


  ScaleBarBuilder.prototype.mmToPt = function (mm) {
    return parseNumber(mm, 4) / MM_PER_PT;
  };


  ScaleBarBuilder.prototype.getOverlayFontSizePt = function (overlay) {
    if (!overlay || typeof overlay !== 'object') {
      return 12;
    }
    var pt = parseNumber(overlay.fontSizePt, NaN);
    if (!Number.isFinite(pt)) {
      var legacyMm = parseNumber(overlay.fontSizeMm, NaN);
      if (Number.isFinite(legacyMm)) {
        pt = this.mmToPt(legacyMm);
      } else {
        pt = 12;
      }
    }
    return clamp(pt, 4, 144);
  };


  ScaleBarBuilder.prototype.getOverlayFontSizeMm = function (overlay) {
    return this.ptToMm(this.getOverlayFontSizePt(overlay));
  };


  ScaleBarBuilder.prototype.normalizeRotation = function (deg) {
    var out = parseNumber(deg, 0) % 360;
    if (out < 0) {
      out += 360;
    }
    return out;
  };


  ScaleBarBuilder.prototype.snapRotation15 = function (deg) {
    return this.normalizeRotation(Math.round(deg / 15) * 15);
  };


  ScaleBarBuilder.prototype.rotatePoint = function (x, y, cx, cy, deg) {
    var rad = (deg * Math.PI) / 180;
    var cos = Math.cos(rad);
    var sin = Math.sin(rad);
    var dx = x - cx;
    var dy = y - cy;
    return {
      x: cx + dx * cos - dy * sin,
      y: cy + dx * sin + dy * cos
    };
  };


  ScaleBarBuilder.prototype.getRotatedRectCorners = function (xMm, yMm, wMm, hMm, rotationDeg) {
    var cx = xMm + wMm / 2;
    var cy = yMm + hMm / 2;
    return {
      tl: this.rotatePoint(xMm, yMm, cx, cy, rotationDeg),
      tr: this.rotatePoint(xMm + wMm, yMm, cx, cy, rotationDeg),
      br: this.rotatePoint(xMm + wMm, yMm + hMm, cx, cy, rotationDeg),
      bl: this.rotatePoint(xMm, yMm + hMm, cx, cy, rotationDeg),
      cx: cx,
      cy: cy
    };
  };


  ScaleBarBuilder.prototype.getRotatedRectAabb = function (xMm, yMm, wMm, hMm, rotationDeg) {
    var corners = this.getRotatedRectCorners(xMm, yMm, wMm, hMm, rotationDeg);
    var xs = [corners.tl.x, corners.tr.x, corners.br.x, corners.bl.x];
    var ys = [corners.tl.y, corners.tr.y, corners.br.y, corners.bl.y];
    var minX = Math.min.apply(Math, xs);
    var maxX = Math.max.apply(Math, xs);
    var minY = Math.min.apply(Math, ys);
    var maxY = Math.max.apply(Math, ys);
    return {
      x: minX,
      y: minY,
      w: maxX - minX,
      h: maxY - minY,
      corners: corners
    };
  };


  ScaleBarBuilder.prototype.clampRotatedRectCenterToPrintable = function (centerXmm, centerYmm, wMm, hMm, rotationDeg, paper) {
    var printable = this.getPrintableRect(paper, 0);
    var halfW = Math.max(0.1, wMm / 2);
    var halfH = Math.max(0.1, hMm / 2);
    var rad = (parseNumber(rotationDeg, 0) * Math.PI) / 180;
    var absCos = Math.abs(Math.cos(rad));
    var absSin = Math.abs(Math.sin(rad));

    var extentX = absCos * halfW + absSin * halfH;
    var extentY = absSin * halfW + absCos * halfH;

    var minCx = printable.minX + extentX;
    var maxCx = printable.maxX - extentX;
    var minCy = printable.minY + extentY;
    var maxCy = printable.maxY - extentY;

    return {
      x: clamp(centerXmm, minCx, maxCx),
      y: clamp(centerYmm, minCy, maxCy)
    };
  };


  ScaleBarBuilder.prototype.angleFromCenterDeg = function (cx, cy, px, py) {
    return (Math.atan2(py - cy, px - cx) * 180) / Math.PI;
  };


  ScaleBarBuilder.prototype.getPaper = function () {
    var base = PAPER_SIZES[this.state.paperSize] || PAPER_SIZES.A4;
    if (this.state.paperOrientation === 'landscape') {
      return {
        key: this.state.paperSize,
        label: base.label.replace(')', ', landscape)'),
        width: base.height,
        height: base.width
      };
    }
    return {
      key: this.state.paperSize,
      label: base.label.replace(')', ', portrait)'),
      width: base.width,
      height: base.height
    };
  };


  ScaleBarBuilder.prototype.getBasePaper = function () {
    return PAPER_SIZES[this.state.paperSize] || PAPER_SIZES.A4;
  };


  ScaleBarBuilder.prototype.getJsPdfFormat = function () {
    var key = this.state.paperSize;
    if (Object.prototype.hasOwnProperty.call(JSPDF_PAPER_FORMATS, key)) {
      return JSPDF_PAPER_FORMATS[key];
    }
    var base = this.getBasePaper();
    return [base.width, base.height];
  };


  ScaleBarBuilder.prototype.getMaxExportCanvasDimension = function () {
    if (Number.isFinite(this._maxExportCanvasDimension) && this._maxExportCanvasDimension > 0) {
      return this._maxExportCanvasDimension;
    }

    var fallback = 4096;
    var probe = document.createElement('canvas');
    var gl = probe.getContext('webgl') || probe.getContext('experimental-webgl');
    if (!gl || typeof gl.getParameter !== 'function') {
      this._maxExportCanvasDimension = fallback;
      return this._maxExportCanvasDimension;
    }

    var limit = parseNumber(gl.getParameter(gl.MAX_TEXTURE_SIZE), fallback);
    if (!Number.isFinite(limit) || limit < 1024) {
      limit = fallback;
    }
    this._maxExportCanvasDimension = Math.round(clamp(limit, 2048, 16384));
    return this._maxExportCanvasDimension;
  };


  ScaleBarBuilder.prototype.getSafeExportDpi = function (targetDpi, paper) {
    var requestedDpi = clamp(parseNumber(targetDpi, 300), 72, 1200);
    var maxDim = this.getMaxExportCanvasDimension();
    var maxDpiByWidth = (maxDim * MM_PER_IN) / Math.max(1, paper.width);
    var maxDpiByHeight = (maxDim * MM_PER_IN) / Math.max(1, paper.height);
    var safeDpi = Math.min(requestedDpi, maxDpiByWidth, maxDpiByHeight);
    return clamp(Math.floor(safeDpi), 96, requestedDpi);
  };


  ScaleBarBuilder.prototype.getTagAssetBase = function () {
    return this.runtimeConfig.tagAssetsBase;
  };


  ScaleBarBuilder.prototype.normalizeSnapGridDivisions = function (value) {
    var raw = Math.round(parseNumber(value, 1));
    return clamp(raw, 1, 16);
  };


  ScaleBarBuilder.prototype.getSnapGridMajorStepMm = function () {
    return this.normalizeSnapGridMmForUnit(parseNumber(this.state.snapGridMm, 5), this.state.unitSystem);
  };


  ScaleBarBuilder.prototype.getGridSubdivisionCount = function () {
    return this.normalizeSnapGridDivisions(this.state.snapGridDivisions);
  };


  ScaleBarBuilder.prototype.getGridStepMm = function () {
    var majorStepMm = this.getSnapGridMajorStepMm();
    var divisions = this.getGridSubdivisionCount();
    return majorStepMm / Math.max(1, divisions);
  };


  ScaleBarBuilder.prototype.normalizeSnapGridMmForUnit = function (gridMm, unitSystem) {
    var rawMm = Math.max(0.1, parseNumber(gridMm, 5));
    if (unitSystem === 'imperial') {
      var inches = rawMm / MM_PER_IN;
      var snappedInches = Math.max(0.25, Math.round(inches * 4) / 4);
      return clamp(snappedInches * MM_PER_IN, 0.25 * MM_PER_IN, 4 * MM_PER_IN);
    }
    return clamp(rawMm, 0.5, 100);
  };


  ScaleBarBuilder.prototype.getUnitScaleDefaults = function (unitSystem) {
    if (unitSystem === 'imperial') {
      return {
        snapGridMm: 1 * MM_PER_IN,
        snapGridDivisions: 4,
        referenceUnitWidthMm: 1 * MM_PER_IN,
        referenceUnitHeightMm: 0.25 * MM_PER_IN,
        description: 'Snap 1 in with 4 divisions (0.25 in step), reference unit width 1 in, reference unit height 0.25 in'
      };
    }
    return {
      snapGridMm: 5,
      snapGridDivisions: 1,
      referenceUnitWidthMm: 10,
      referenceUnitHeightMm: 5,
      description: 'Snap 5 mm, reference unit width 10 mm, reference unit height 5 mm'
    };
  };


  ScaleBarBuilder.prototype.applyUnitScaleDefaults = function (unitSystem) {
    var defaults = this.getUnitScaleDefaults(unitSystem);
    this.state.snapGridMm = defaults.snapGridMm;
    this.state.snapGridDivisions = this.normalizeSnapGridDivisions(defaults.snapGridDivisions);
    this.state.checkerWidthCm = defaults.referenceUnitWidthMm / MM_PER_CM;
    this.state.checkerHeightMm = defaults.referenceUnitHeightMm;

    if (!Array.isArray(this.state.references)) {
      return;
    }
    var paper = this.getPaper();
    for (var i = 0; i < this.state.references.length; i += 1) {
      var ref = this.state.references[i];
      if (!ref) {
        continue;
      }
      var prevCellWidth = Math.max(0.2, parseNumber(ref.cellWidthMm, defaults.referenceUnitWidthMm));
      var prevLength = Math.max(prevCellWidth, parseNumber(ref.lengthMm, prevCellWidth));
      var prevCellCount = Math.max(1, Math.round(prevLength / prevCellWidth));
      ref.cellWidthMm = defaults.referenceUnitWidthMm;
      ref.cellHeightMm = defaults.referenceUnitHeightMm;
      ref.lengthMm = Math.max(ref.cellWidthMm, prevCellCount * ref.cellWidthMm);
      ref.unitSystem = unitSystem === 'imperial' ? 'imperial' : 'metric';
      if (!this.isReferenceRowStyle(ref.style || 'checker')) {
        ref.rows = 1;
      }
      this.clampReferenceToPrintable(ref, paper);
    }
  };


  ScaleBarBuilder.prototype.clampReferenceToPrintable = function (ref, paper) {
    if (!ref || !paper) {
      return;
    }
    var printable = this.getPrintableRect(paper, 0);
    var printableW = Math.max(0.2, printable.maxX - printable.minX);
    var printableH = Math.max(0.2, printable.maxY - printable.minY);
    var rows = Math.max(1, Math.round(parseNumber(ref.rows, 1)));
    var cellHeightMm = clamp(parseNumber(ref.cellHeightMm, this.state.checkerHeightMm), 0.2, printableH / rows);
    var totalHeightMm = cellHeightMm * rows;
    var cellWidthMm = clamp(parseNumber(ref.cellWidthMm, this.state.checkerWidthCm * MM_PER_CM), 0.2, printableW);
    var lengthMm = Math.max(cellWidthMm, parseNumber(ref.lengthMm, cellWidthMm));
    var cellCount = Math.max(1, Math.round(lengthMm / cellWidthMm));
    lengthMm = clamp(cellCount * cellWidthMm, cellWidthMm, printableW);
    var centerX = parseNumber(ref.xMm, printable.minX) + lengthMm / 2;
    var centerY = parseNumber(ref.yMm, printable.minY) + totalHeightMm / 2;
    var clampedCenter = this.clampRotatedRectCenterToPrintable(
      centerX,
      centerY,
      lengthMm,
      totalHeightMm,
      this.normalizeRotation(ref.rotationDeg || 0),
      paper
    );
    ref.cellWidthMm = cellWidthMm;
    ref.cellHeightMm = cellHeightMm;
    ref.lengthMm = lengthMm;
    ref.rows = rows;
    ref.xMm = clampedCenter.x - lengthMm / 2;
    ref.yMm = clampedCenter.y - totalHeightMm / 2;
  };


  ScaleBarBuilder.prototype.clampOverlayToPrintable = function (overlay, paper) {
    if (!overlay || !paper) {
      return;
    }
    var widthMm = Math.max(2, parseNumber(overlay.wMm, 2));
    var heightMm = Math.max(2, parseNumber(overlay.hMm, 2));
    var centerX = parseNumber(overlay.xMm, 0) + widthMm / 2;
    var centerY = parseNumber(overlay.yMm, 0) + heightMm / 2;
    var clampedCenter = this.clampRotatedRectCenterToPrintable(
      centerX,
      centerY,
      widthMm,
      heightMm,
      this.normalizeRotation(overlay.rotationDeg || 0),
      paper
    );
    overlay.xMm = clampedCenter.x - widthMm / 2;
    overlay.yMm = clampedCenter.y - heightMm / 2;
  };


  ScaleBarBuilder.prototype.snapCenterToGrid = function (valueMm, gridStepMm, centerMm) {
    var step = gridStepMm || this.getGridStepMm();
    var center = Number.isFinite(centerMm) ? centerMm : 0;
    return center + Math.round((valueMm - center) / step) * step;
  };


  ScaleBarBuilder.prototype.ensureTagSizes = function () {
    if (!Array.isArray(this.state.tagSizesMm)) {
      this.state.tagSizesMm = [];
    }
    for (var i = 0; i < this.state.tagCount; i += 1) {
      var size = parseNumber(this.state.tagSizesMm[i], this.state.tagSizeMm);
      this.state.tagSizesMm[i] = clamp(size, 8, 180);
    }
    this.state.tagSizesMm.length = this.state.tagCount;
  };


  ScaleBarBuilder.prototype.getTagSizeMm = function (index) {
    this.ensureTagSizes();
    if (typeof index === 'number' && index >= 0 && index < this.state.tagSizesMm.length) {
      return clamp(parseNumber(this.state.tagSizesMm[index], this.state.tagSizeMm), 8, 180);
    }
    return clamp(parseNumber(this.state.tagSizeMm, 50), 8, 180);
  };


  ScaleBarBuilder.prototype.getTagCenterKeepoutMm = function (sizeMm) {
    var tagHalfMm = (parseNumber(sizeMm, this.state.tagSizeMm) || this.state.tagSizeMm) / 2;
    var tagPaddingMm = tagHalfMm * CHECKER_TAG_PADDING_FACTOR;
    return tagHalfMm + tagPaddingMm;
  };


  ScaleBarBuilder.prototype.getPrintMarginMm = function () {
    return clamp(parseNumber(this.state.printMarginMm, 5), 0, 40);
  };


  ScaleBarBuilder.prototype.getPrintableRect = function (paper, extraPadMm) {
    var pad = Math.max(0, parseNumber(extraPadMm, 0));
    var base = this.getPrintMarginMm() + pad;
    var maxBaseX = Math.max(0, paper.width / 2 - 0.2);
    var maxBaseY = Math.max(0, paper.height / 2 - 0.2);
    var insetX = Math.min(base, maxBaseX);
    var insetY = Math.min(base, maxBaseY);
    return {
      minX: insetX,
      minY: insetY,
      maxX: paper.width - insetX,
      maxY: paper.height - insetY
    };
  };


  ScaleBarBuilder.prototype.clampRectTopLeftToPrintable = function (xMm, yMm, wMm, hMm, paper) {
    var rect = this.getPrintableRect(paper, 0);
    var maxX = Math.max(rect.minX, rect.maxX - Math.max(0, wMm));
    var maxY = Math.max(rect.minY, rect.maxY - Math.max(0, hMm));
    return {
      x: clamp(xMm, rect.minX, maxX),
      y: clamp(yMm, rect.minY, maxY)
    };
  };


  ScaleBarBuilder.prototype.getSceneBounds = function (paper, options) {
    var opts = options || {};
    var moveInfoOverlay = opts.moveInfoOverlay !== false;
    var includeTags = opts.includeTags !== false;
    var includeOverlays = opts.includeOverlays !== false;
    var includeReferences = opts.includeReferences !== false;
    var bounds = {
      minX: paper.width,
      minY: paper.height,
      maxX: 0,
      maxY: 0
    };

    var has = false;
    if (includeTags) {
      for (var i = 0; i < this.state.tagCount; i += 1) {
        var tag = this.state.tags[i];
        if (!tag) {
          continue;
        }
        var half = this.getTagSizeMm(i) / 2;
        bounds.minX = Math.min(bounds.minX, tag.xMm - half);
        bounds.maxX = Math.max(bounds.maxX, tag.xMm + half);
        bounds.minY = Math.min(bounds.minY, tag.yMm - half);
        bounds.maxY = Math.max(bounds.maxY, tag.yMm + half);
        has = true;
      }
    }

    if (includeOverlays) {
      var overlays = this.state.overlayItems || [];
      for (var j = 0; j < overlays.length; j += 1) {
        var ov = overlays[j];
        if (!ov || !ov.visible) {
          continue;
        }
        if (!moveInfoOverlay && ov.type === 'info') {
          continue;
        }
        var ow = Math.max(2, parseNumber(ov.wMm, 2));
        var oh = Math.max(2, parseNumber(ov.hMm, 2));
        var ox = parseNumber(ov.xMm, 0);
        var oy = parseNumber(ov.yMm, 0);
        bounds.minX = Math.min(bounds.minX, ox);
        bounds.maxX = Math.max(bounds.maxX, ox + ow);
        bounds.minY = Math.min(bounds.minY, oy);
        bounds.maxY = Math.max(bounds.maxY, oy + oh);
        has = true;
      }
    }

    if (includeReferences) {
      var refs = this.state.references || [];
      for (var k = 0; k < refs.length; k += 1) {
        var ref = refs[k];
        if (!ref) {
          continue;
        }
        var rw = Math.max(0.2, parseNumber(ref.lengthMm, 0.2));
        var rh = Math.max(0.2, parseNumber(ref.cellHeightMm, 0.2)) * Math.max(1, Math.round(parseNumber(ref.rows, 1)));
        bounds.minX = Math.min(bounds.minX, ref.xMm);
        bounds.maxX = Math.max(bounds.maxX, ref.xMm + rw);
        bounds.minY = Math.min(bounds.minY, ref.yMm);
        bounds.maxY = Math.max(bounds.maxY, ref.yMm + rh);
        has = true;
      }
    }

    if (!has) {
      var printable = this.getPrintableRect(paper, 0);
      return { minX: printable.minX, minY: printable.minY, maxX: printable.maxX, maxY: printable.maxY };
    }
    return bounds;
  };


  ScaleBarBuilder.prototype.translateScene = function (dxMm, dyMm, options) {
    var opts = options || {};
    var moveInfoOverlay = opts.moveInfoOverlay !== false;
    var maxOverflowMm = clamp(parseNumber(opts.maxOverflowMm, 0), 0, 20);
    var paper = this.getPaper();
    var bounds = this.getSceneBounds(paper, opts);
    var printable = this.getPrintableRect(paper, 0);
    var clampedDx = clamp(dxMm, printable.minX - bounds.minX - maxOverflowMm, printable.maxX - bounds.maxX + maxOverflowMm);
    var clampedDy = clamp(dyMm, printable.minY - bounds.minY - maxOverflowMm, printable.maxY - bounds.maxY + maxOverflowMm);

    if (Math.abs(clampedDx) < 0.0001 && Math.abs(clampedDy) < 0.0001) {
      return { dx: 0, dy: 0 };
    }

    this.switchToManualMode();
    for (var i = 0; i < this.state.tagCount; i += 1) {
      this.state.tags[i].xMm += clampedDx;
      this.state.tags[i].yMm += clampedDy;
    }
    for (var j = 0; j < this.state.overlayItems.length; j += 1) {
      var ov = this.state.overlayItems[j];
      if (!moveInfoOverlay && ov && ov.type === 'info') {
        continue;
      }
      ov.xMm = parseNumber(ov.xMm, 0) + clampedDx;
      ov.yMm = parseNumber(ov.yMm, 0) + clampedDy;
    }
    for (var k = 0; k < this.state.references.length; k += 1) {
      var ref = this.state.references[k];
      ref.xMm += clampedDx;
      ref.yMm += clampedDy;
    }
    if (this.state.homePoint && typeof this.state.homePoint === 'object') {
      this.state.homePoint.xMm = clamp(this.state.homePoint.xMm + clampedDx, 0, paper.width);
      this.state.homePoint.yMm = clamp(this.state.homePoint.yMm + clampedDy, 0, paper.height);
    }
    this.state.logoAutoPlacement = false;
    return { dx: clampedDx, dy: clampedDy };
  };


  ScaleBarBuilder.prototype.getSceneOverflowMm = function (options) {
    var paper = this.getPaper();
    var bounds = this.getSceneBounds(paper, options || {});
    var printable = this.getPrintableRect(paper, 0);
    var overflowLeft = Math.max(0, printable.minX - bounds.minX);
    var overflowRight = Math.max(0, bounds.maxX - printable.maxX);
    var overflowTop = Math.max(0, printable.minY - bounds.minY);
    var overflowBottom = Math.max(0, bounds.maxY - printable.maxY);
    return Math.max(overflowLeft, overflowRight, overflowTop, overflowBottom);
  };


  ScaleBarBuilder.prototype.getSceneVisualAlpha = function () {
    if (!this.state.cutMoveUnlocked || this.isExportRender) {
      return 1;
    }
    var overflowMm = this.getSceneOverflowMm({
      moveInfoOverlay: false,
      includeOverlays: false,
      includeReferences: false
    });
    if (overflowMm <= 0.01) {
      return 1;
    }
    var fadeWindowMm = 10;
    var ratio = clamp(overflowMm / fadeWindowMm, 0, 1);
    return clamp(1 - (ratio * 0.6), 0.4, 1);
  };


  

  ScaleBarBuilder.prototype.updateViewportLayout = function () {
    if (!this.root) {
      return;
    }
    var rect = this.root.getBoundingClientRect();
    var viewportHeight = 0;
    if (window.visualViewport && isFinite(parseNumber(window.visualViewport.height, NaN))) {
      viewportHeight = parseNumber(window.visualViewport.height, 0);
    }
    viewportHeight = Math.max(
      viewportHeight,
      window.innerHeight || 0,
      document.documentElement ? document.documentElement.clientHeight : 0
    );
    var topOffset = Math.max(0, rect.top || 0);
    var availableHeight = clamp(viewportHeight - topOffset - 8, 360, 2600);
    this.root.style.setProperty('--sbb-available-height', Math.round(availableHeight) + 'px');
  };


  ScaleBarBuilder.prototype.resizeCanvas = function () {
    var ratio = window.devicePixelRatio || 1;
    var cssWidth = Math.max(1, this.canvas.clientWidth || Math.round(this.canvas.getBoundingClientRect().width));
    var cssHeight = Math.max(1, this.canvas.clientHeight || Math.round(this.canvas.getBoundingClientRect().height));

    this.canvas.width = Math.max(300, Math.round(cssWidth * ratio));
    this.canvas.height = Math.max(260, Math.round(cssHeight * ratio));
    this.pixelRatio = ratio;
  };


  ScaleBarBuilder.prototype.getCanvasPixelScale = function () {
    var cssW = Math.max(1, this.canvas.clientWidth || Math.round(this.canvas.getBoundingClientRect().width));
    var cssH = Math.max(1, this.canvas.clientHeight || Math.round(this.canvas.getBoundingClientRect().height));
    return {
      scaleX: this.canvas.width / cssW,
      scaleY: this.canvas.height / cssH
    };
  };


  ScaleBarBuilder.prototype.eventToCanvasPixels = function (event) {
    if (!event || !this.canvas) {
      return null;
    }
    var rect = this.canvas.getBoundingClientRect();
    var scale = this.getCanvasPixelScale();
    var localCssX = event.clientX - rect.left - this.canvas.clientLeft;
    var localCssY = event.clientY - rect.top - this.canvas.clientTop;
    return {
      xPx: localCssX * scale.scaleX,
      yPx: localCssY * scale.scaleY
    };
  };


  ScaleBarBuilder.prototype.paperToCanvas = function (xMm, yMm, view) {
    return {
      x: view.originX + xMm * view.scale,
      y: view.originY + yMm * view.scale
    };
  };


  ScaleBarBuilder.prototype.canvasToPaper = function (event, view) {
    var activeView = view || this.view;
    if (!activeView) {
      return null;
    }
    var pixelPoint = this.eventToCanvasPixels(event);
    if (!pixelPoint) {
      return null;
    }
    var xPx = pixelPoint.xPx;
    var yPx = pixelPoint.yPx;
    return {
      x: (xPx - activeView.originX) / activeView.scale,
      y: (yPx - activeView.originY) / activeView.scale,
      xPx: xPx,
      yPx: yPx
    };
  };


  ScaleBarBuilder.prototype.snapMm = function (valueMm, centerMm) {
    if (!this.state.snapEnabled || !this.dragState) {
      return valueMm;
    }
    var grid = this.getGridStepMm();
    return this.snapCenterToGrid(valueMm, grid, centerMm);
  };

})();
